import THREE from "three";
import { LmvBox3 as Box3 } from "./LmvBox3";
import { Quaternion } from "three";
import { LmvMatrix4 as Matrix4 } from "./LmvMatrix4";
import { LmvVector3 as Vector3 } from "./LmvVector3";
import { InstanceCollectionBase } from "./InstanceCollectionBase";
import { MeshFlags } from "./MeshFlags";
import { logger } from "../../logger/Logger";
import { allocateUintArray, reallocateUintArrayMaybe } from "./IntArray";
import { INVALID_MAT_ID, USE_WEBGPU } from "../globals";
import { VertexEnumerator } from "./VertexEnumerator";
import { ReusableIds } from "./ReusableIds";
import { BufferGeometry, BufferGeometryUtils } from "./BufferGeometry";
import { Node3D } from "./scene-graph/Node3D";
import { FeatureFlags, InternalFeatureFlags } from "../../application/FeatureFlags";
import { TextureLoader } from "../../file-loaders/main/TextureLoader";

const _tmpMatrix = new Matrix4(true);
const _tmpBox = new Box3();
const _tmpRot = new Quaternion();
const _tmpPos = new Vector3();
const _tmpScale = new Vector3();

/** @import { InstancesCallback, GeometryChangedCallback } from "./InstanceCollectionBase" */
/** @import { Model } from "../../application/Model" */
/** @import { Viewer3DImpl } from "../../application/Viewer3DImpl" */
/** @import { FragmentsObject } from "./InstanceCollectionBase" */

// Used in FragmentList::getWorldMatrix.
// Allocate a second temp variable here because the input can be the class scoped temporary matrix in some call sequences.
const tmp = new Matrix4();

/**
 * @callback HighlightingCategoryMappingCallback
 * @param {number} dbId - The dbId of the object for which the highlighting category is requested.
 * @returns {number} The highlighting category for the given dbId. 0 is reserved for "no highlight".
 *
 * @private
 */

/////////////////////////////////////////////////////////////////////
// helper functions for FragmentList

// A scratch object that we fill in and return in the case we don't use THREE.Mesh for persistent storage.
// If the caller needs to hold on to the mesh outside the callback scope, it has to clone it.
function createScratchMesh() {
  var m = new THREE.Mesh();
  m.isTemp = true;
  m.dbId = 0;
  m.modelId = 0;
  m.fragId = -1;
  m.hide = false;
  m.isLine = false;
  m.isWideLine = false;
  m.isPoint = false;
  m.frustumCulled = false;
  m.highlightingCategory = 0;

  return m;
}
/////////////////////////////////////////////////////////////////////

/**
 * Represents the full list of all instances of a model, and provides functions to access and manipulate them.
 *
 * @alias Autodesk.Viewing.Scene.InstanceCollection3D
 */
export class InstanceCollection3D extends InstanceCollectionBase {

  // Number of fragments that are marked as moved. This is used to determine if any fragment transformation changed.
  movedFragments = 0;

  // map from material ids to THREE.ShaderMaterial instances
  materialmap = {};
  materialIdMap = {};
  nextMaterialId = 1; // 0 is INVALID_MAT_ID
  materialIdMapOriginal = null;

  geomids = null;
  materialids = null;

  // Visibility of lines and points is independent of per-fragment visibility flags
  linesHidden = false;
  pointsHidden = false;

  // If animation is used, this is a Float32Array storing 10 floats per fragment to describe scale (3), rotation (4), and translation (3).
  // See this.updateAnimTransform.
  animxforms = null;

  /** @type {InstancesCallback[]} */
  _transformChangedCallbacks = [];

  /** @type {GeometryChangedCallback[]} */
  _geometryChangedCallbacks = [];

  /** @type {InstancesCallback[]} */
  _instancesAddedCallbacks = [];

  /** @type {InstancesCallback[]} */
  _instancesRemovedCallbacks = [];

  _highlightingCategoryChangedCallbacks = [];

  /**
   * @type {HighlightingCategoryMappingCallback}
   * Client-controlled callback to pass client-specific highlighting categories to the renderer.
   */
  _highlightingCategoryMapping = null;

  /** @type {ReusableIds} */
  _instanceIdGenerator;

  /** @type {Node3D} */
  #sceneGraph = null;

  /** @type {boolean} Whether the model bounding box needs to be recomputed */
  #modelBBoxDirty = false;

  constructor(fragments, geomList, modelId, num_materials, isOTG, model, viewer3DImpl) {
    super(fragments, modelId);
    this.geoms = geomList;
    this.model = model;
    this._viewer3DImpl = viewer3DImpl;

    if (this.fragments === undefined) {
      this.fragments = {
        length: 0,
        fragId2dbId: []
      };
    }

    // 3D SVF files are of known initial size and known world bounds.
    // NOTE: There is a bug here when we have an SVF file with known zero fragments -- it will go into the slower non-fixed size code path. But it doesn't matter, because it's an empty file.
    const initialSize = this.fragments.length;
    this.isFixedSize = initialSize > 0;
    if (this.isFixedSize) {
      this.boxes = this.fragments.boxes; // Float32Array, stores Boxes as 6 floats per fragment (after applying mesh matrix)
      this.transforms = this.fragments.transforms; // Float32Array, stores transforms as 12 floats per fragment (Matrix4 with omitted last row)
    } else {
      this.boxes = null;
      this.transforms = null;

      this._instanceIdGenerator = new ReusableIds(initialSize);
    }

    if (isOTG) {
      this.materialids = allocateUintArray(initialSize, num_materials + 1);
      // Will be filled by the loader. Note that with otg a Geometry's svfid is a global id, while the geomids here are model-local.
      this.geomids = this.fragments.geomDataIndexes;
      // will be initialized by the loader and updated by setMesh
      this.vizflags = this.fragments.visibilityFlags;
    } else {
      this.geomids = new Int32Array(initialSize); // geomid per fragId. geomids are resolved by this.geoms.GetGeometry(geomid) to obtain BufferGeometry.
      this.materialids = new Int32Array(initialSize); // material per fragId. matIds  are resolved by this.materialmap[matId]
      this.vizflags = new Uint16Array(initialSize); // visibility/highlight mode flags

      if (this.fragments.visibilityFlags) {
        this.vizflags.set(this.fragments.visibilityFlags);
      } else {
        this.vizflags.fill(MeshFlags.MESH_VISIBLE);
      }
    }
  }

  /**
   * Set mesh for a fragment, replacing any temporary previous one.
   * @param {number} fragId - Fragment ID
   * @param {Object} meshinfo - Object as defined in Viewer3DImpl.setupMesh(..). Contains:
   *      geometry: instanceof BufferGeometry
   *      material: instance of THREE.Material
   *      matrix:   instanceof THREE.Matrix4
   *      bbox:     Optional world space bounding box
   *      isLine:   bool to mark line geometry
   *      isWideLine: bool to mark wide line geometry
   *      isPoint:   bool to mark point geometry
   * @param {bool} updateFragmentData - If true, this.bbox and this.transforms is also updated for this fragment.
   *      Only allowed if this.isFixedSize==true. (otherwise, this.boxes and this.transforms is null)
   */
  setMesh(fragId, meshInfo, updateFragmentData) {
    if (fragId >= this.vizflags.length) {
      this.resize(fragId + 1);
    }

    // When not using THREE.Mesh, store ids of BufferGeometry and material instead
    // In case of OTG, this.geomids is filled directly by the loader, nothing needs to be done here.
    if (!meshInfo.geometry.hash) {
      this.geomids[fragId] = meshInfo.geometry.svfid;
    }

    this.setMaterial(fragId, meshInfo.material, true);

    // Don't override the visibility flag which could be set before geometry is ready.
    // This can improve the performance when streaming geometry and rendering happen together.
    let typeFlags = this.isFixedSize ? 0 : MeshFlags.MESH_VISIBLE;
    if (meshInfo.isLine) {
      typeFlags |= MeshFlags.MESH_ISLINE;
    } else if (meshInfo.isWideLine) {
      typeFlags |= MeshFlags.MESH_ISWIDELINE;
    } else if (meshInfo.isPoint) {
      typeFlags |= MeshFlags.MESH_ISPOINT;
    }

    this.setFlagFragment(
      fragId,
      typeFlags,
      true);

    if (updateFragmentData && this.transforms && this.boxes) {
      // Update transform and bb
      const transform = meshInfo.matrix;

      // Copy the transform to the fraglist array
      // We store in column-major order like the elements of the Matrix4, but skip row 3.
      const i = fragId * 12;
      const cur = transform.elements;
      const orig = this.transforms;
      orig[i] = cur[0];
      orig[i + 1] = cur[1];
      orig[i + 2] = cur[2];
      orig[i + 3] = cur[4];
      orig[i + 4] = cur[5];
      orig[i + 5] = cur[6];
      orig[i + 6] = cur[8];
      orig[i + 7] = cur[9];
      orig[i + 8] = cur[10];
      orig[i + 9] = cur[12];
      orig[i + 10] = cur[13];
      orig[i + 11] = cur[14];

      // When using Otg, computed bboxes are only used until we get the actual ones from fragments_extra.
      // Once loaded, they must not be overwritten by computed ones (which are too large in some cases).
      if (!this.fragments.dontComputeBoxes) {
        this._updateBBox(fragId, transform, meshInfo.bbox ?? meshInfo.geometry?.boundingBox, !!meshInfo.bbox);
      }
    }

    for (const callback of this._meshSetCallbacks) {
      callback(fragId);
    }
  }

  /**
   * Set/unset flag of a fragment.
   * Note: Changing MESH_VISIBLE requires to update allVisibleDirty as well => Use setVisibility() for this case.
   * @param {number} fragId - Fragment ID.
   * @param {number} flag - Must be one of the flags defined at the beginning of this file, e.g., MESH_HIGHLIGHTED.
   * @returns {bool} False if nothing changed.
   */
  setFlagFragment(fragId, flag, value) {
    if (flag & MeshFlags.MESH_MOVED) {
      this.movedFragments += value ? 1 : -1;
    }

    return super.setFlagFragment(fragId, flag, value);
  }

  /**
   * Marks all lines as visible or hidden.
   * Works like this.setFlagGlobal(MESH_HIDE, hide), but only affects fragments with MESH_ISLINE flag.
   * @param {bool} hide - Desired visibility status.
   */
  hideLines(hide) {
    // Inform other code (e.g. the renderer) about the visibility change.
    if (hide !== this.linesHidden) {
      for (const callback of this._visibilityDirtyCallbacks) {
        callback();
      }
    }

    this.linesHidden = hide;
  }

  /**
   * Marks all points as visible or hidden.
   * Works like this.setFlagGlobal(MESH_HIDE, hide), but only affects fragments with MESH_ISPOINT flag.
   * @param {bool} hide - Desired visibility status.
   */
  hidePoints(hide) {
    // Inform other code (e.g. the renderer) about the visibility change.
    if (hide !== this.pointsHidden) {
      for (const callback of this._visibilityDirtyCallbacks) {
        callback();
      }
    }

    this.pointsHidden = hide;
  }

  /**
   * Checks visibility of a fragment.
   * @param {number} frag - Fragment ID.
   * @returns {bool} True if the fragment is visible and not highlighted nor hidden.
   */
  isFragVisible(frag) {
    return super.isFragVisible(frag) && !this.isFragHiddenLineOrPoint(frag);
  }

  /**
   * Checks whether a fragment is a hidden line or hidden point
   * @param {number} frag - Fragment ID
   * @returns {bool} whether a fragment is a hidden line or hidden point
   */
  isFragHiddenLineOrPoint(frag) {
    const isHiddenLine = this.linesHidden && (this.isLine(frag) || this.isWideLine(frag));
    const isHiddenPoint = this.pointsHidden && this.isPoint(frag);
    return isHiddenLine || isHiddenPoint;
  }

  isLine(frag) {
    return !!(this.vizflags[frag] & MeshFlags.MESH_ISLINE /*MESH_VISIBLE|MESH_HIGHLIGHTED*/);
  }

  isWideLine(frag) {
    return this.isFlagSet(frag, MeshFlags.MESH_ISWIDELINE);
  }

  isPoint(frag) {
    return this.isFlagSet(frag, MeshFlags.MESH_ISPOINT);
  }

  /**
   * Loads the given fragments
   *
   * @param {Iterable.<number>} fragIds - IDs of fragments to load
   * @param {MeshFlags} loadedFlag - A flag that will be unset on the fragment
   * @returns {Array<Number>} FragIds that where loaded
   */
  load(fragIds, loadedFlag) {
    let fragmentsLoaded = [];
    for (const fragId of fragIds) {

      // Update the fragment flag
      const changed = this.setFlagFragment(fragId, loadedFlag, false);

      // Did the status change?
      if (changed && this.isFragmentLoaded(fragId)) {
        this.setFragOff(fragId, false);
        fragmentsLoaded.push(fragId);
      }
    }

    return fragmentsLoaded;
  }

  /**
   * Unloads given fragments and their geometry if not needed anymore
   *
   * @param {Iterable.<number>} fragIds - IDs of fragments to unload
   * @param {OtgResourceCache} [geomCache] - Optional geomcache used only by the OTG loader
   * @returns {number} Number of fragments that where unloaded
   */
  unload(fragIds, geomCache) {
    let fragmentsUnloaded = 0;
    for (const fragId of fragIds) {
      if (this.setFlagFragment(fragId, MeshFlags.MESH_NOTLOADED, true)) {
        this.setFragOff(fragId, true);
        this.geoms.removeInstance(this.geomids[fragId], geomCache);
        ++fragmentsUnloaded;
      }
    }

    return fragmentsUnloaded;
  }

  /**
   * Provides an actual mesh for specific fragment.
   * NOTE: The returned value is volatile and will be overwritten on next call!
   * @param {number} fragId - Fragment ID.
   * @returns {THREE.Mesh} Mesh for the given fragment.
   */
  getVizmesh(fragId) {
    const m = this.scratchMesh = this.scratchMesh || createScratchMesh();

    // init temp mesh object from geometry, material etc.
    m.geometry = this.getGeometry(fragId); // BufferGeometry
    m.material = this.getMaterial(fragId); // THREE.ShaderMaterial
    m.dbId = this.getDbIds(fragId);
    m.modelId = this.modelId;
    m.highlightingCategory = this._highlightingCategoryMapping?.(m.dbId) || 0;
    m.fragId = fragId;
    m.visible = true;
    m.isLine = this.isLine(fragId);
    m.isWideLine = this.isWideLine(fragId);
    m.isPoint = this.isPoint(fragId);
    m.hide = this.isFragOff(fragId);
    m.themingColor = this.db2ThemingColor[m.dbId];

    this.getWorldMatrix(fragId, m.matrixWorld);

    return m;
  }

  /**
   * Returns the material for a specific instance.
   * @param {number} instanceId
   * @returns {*} The material for the given instance, or undefined if no material is currently assigned to the instance.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.getMaterial
   */
  getMaterial(instanceId) {
    return this.materialIdMap[this.materialids[instanceId]];
  }

  hasMaterial(instanceId) {
    return this.materialids[instanceId] !== INVALID_MAT_ID;
  }

  storeOriginalMaterials() {
    this.materialIdMapOriginal = [];
    let mat;
    let matId;
    for (let i = 0; i < this.fragments.length; i++) {
      mat = this.getMaterial(i);
      if (mat && mat.id !== undefined) {
        matId = mat.id;
        this.materialIdMapOriginal.push(matId);
      } else {
        // Push null for fragments without materials to maintain index consistency.
        // This is a workaround since we currently don't apply materials to missing fragments.
        this.materialIdMapOriginal.push(null);
      }
    }
  }

  restoreOriginalMaterials() {
    /**
    *   Return if function called before original materials stored.
    *   This fix covers the following scenario:
    *   1. Navisworks documents have Viewpoints. Viewpoints may contain or don't contain color overrides
    *       (color overrides can be referenced as materials).
    *   2. `storeOriginalMaterials` saved once Viewpoint with color overrides selected.
    *   3. We may need to reset color overrides, in order to restore default material settings.
    *   4. The user cannot know if a given Viewpoint has a color override, thus, he may invoke `restoreOriginalMaterials`
    *       at different stages.It may happen in the Document Browser extension or any other UI component (ACC uses the React component).
    *   Having heuristics that define if the last selected Viewpoint had any overrides can be more fragile than
    *   simple protection as in the proposal above, since it should be applied in every case where `restoreOriginalMaterials` might be invoked.
    */
    if (!this.materialIdMapOriginal) {
      return;
    }

    let matId;
    for (let i = 0; i < this.materialIdMapOriginal.length; i++) {
      matId = this.materialIdMapOriginal[i];
      // Skip null entries (fragments that didn't have materials)
      if (matId !== null && this.materialIdMap[this.materialmap[matId]]) {
        this.setMaterial(i, this.materialIdMap[this.materialmap[matId]]);
      }
    }
  }

  /**
   * Returns the geometry for a specific instance.
   * @param {number} instanceId
   * @returns {Autodesk.Viewing.Scene.BufferGeometry|undefined} The geometry for the given instance, or undefined no geometry is currently assigned to the instance.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.getGeometry
   */
  getGeometry(instanceId) {
    return this.geoms.getGeometry(this.geomids[instanceId]);
  }


  hasGeometry(instanceId) {
    return this.geoms.hasGeometry(this.geomids[instanceId]);
  }

  getGeometryId(instanceId) {
    return this.geomids[instanceId];
  }

  /**
   * Sets the material for a specific instance.
   *
   * @param {number} instanceId
   * @param {BasicMaterial|PhongMaterial} material - The material to set for the given instance.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.setMaterial
   */
  setMaterial(instanceId, material, fromLoader = false) {
    var matId = this.materialmap[material.id];
    if (!matId) {
      this.addMaterial(material);

      //Material.id is global, hence we can't expect it to be a small
      //integer. Hence the incrementing counter indirection.
      matId = this.nextMaterialId++;

      this.materialids = reallocateUintArrayMaybe(this.materialids, matId);

      this.materialIdMap[matId] = material;

      //remember our local ID for this global material
      this.materialmap[material.id] = matId;
    }

    this.materialids[instanceId] = matId;

    for (const callback of this._materialChangedCallbacks) {
      callback(instanceId, material, fromLoader);
    }
  }

  /**
   * Returns the number of instances in the collection.
   * @returns {number} The number of instances.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.getCount
   */
  getCount() {
    return this.fragments.length;
  }

  /**
   * Returns the dbId of a specific instance.
   * @param {number} instanceId
   * @returns {number} The dbId of the given instance.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.getDbId
   */
  getDbId(instanceId) {
    return this.fragments.fragId2dbId[instanceId];
  }

  dispose() {
    // Delete all geometry data immediately (see WebGLRenderer.deallocateGeometry)
    this.geoms.dispose();
  }

  dtor(geomCache) {
    this.dispose();
    super.dtor();

    this.scratchMesh = null;

    this.geoms.dtor(geomCache);
    this.geoms = null;
    this.boxes = null;
    this.transforms = null;

    this.geomids = null;
    this.materialids = null;

    this.materialmap = null;
    this.materialIdMap = null;

    this.animxforms = null;
  }

  setAllVisibility(value) {
    this.setFlagGlobal(MeshFlags.MESH_VISIBLE, value);
    super.setAllVisibility(value);
  }

  /**
   * Updates animation transform of a specific fragment.
   * Note:
   *      - If scale/rotation/translation are all null, the call resets the whole transform, i.e., no anim transform is assigned anymore.
   *      - Leaving some of them null means to leave them unchanged.
   * @param {number} fragId - Fragment ID.
   * @param {Vector3=} scale
   * @param {Quaternion=} rotationQ
   * @param {Vector3=} translation
   */
  updateAnimTransform(fragId, scale, rotationQ, translation) {
    var ax = this.animxforms;
    var off;

    //Allocate animation transforms on first use.
    if (!ax) {
      var count = this.getCount();
      ax = this.animxforms = new Float32Array(10 * count); //3 scale + 4 rotation + 3 translation
      for (var i = 0; i < count; i++) {
        // get start index of the anim transform of fragment i
        off = i * 10;

        // init as identity transform
        ax[off] = 1; // scale.x
        ax[off + 1] = 1; // scale.y
        ax[off + 2] = 1; // scale.z
        ax[off + 3] = 0; // rot.x
        ax[off + 4] = 0; // rot.y
        ax[off + 5] = 0; // rot.z
        ax[off + 6] = 1; // rot.w
        ax[off + 7] = 0; // trans.x
        ax[off + 8] = 0; // trans.y
        ax[off + 9] = 0; // trans.z
      }
    }

    off = fragId * 10;
    var moved = false;

    if (scale) {
      ax[off] = scale.x;
      ax[off + 1] = scale.y;
      ax[off + 2] = scale.z;
      moved = true;
    }

    if (rotationQ) {
      ax[off + 3] = rotationQ.x;
      ax[off + 4] = rotationQ.y;
      ax[off + 5] = rotationQ.z;
      ax[off + 6] = rotationQ.w;
      moved = true;
    }

    if (translation) {
      ax[off + 7] = translation.x;
      ax[off + 8] = translation.y;
      ax[off + 9] = translation.z;
      moved = true;
    }

    // Set MESH_MOVED if an animation transform has been assigned. Just if scale/rotation/translation are all null, unset the flag.
    this.setFlagFragment(fragId, MeshFlags.MESH_MOVED, moved);

    //Assume that if we are called with null everything the caller wants to reset the transform.
    if (!moved) {
      // reset to identity transform
      ax[off] = 1;
      ax[off + 1] = 1;
      ax[off + 2] = 1;
      ax[off + 3] = 0;
      ax[off + 4] = 0;
      ax[off + 5] = 0;
      ax[off + 6] = 1;
      ax[off + 7] = 0;
      ax[off + 8] = 0;
      ax[off + 9] = 0;
    }

    for (const callback of this._transformChangedCallbacks) {
      callback(fragId);
    }
  }

  /**
   * Returns animation transform of a specific fragment.
   * @param {number} fragId - Fragment ID.
   * @param {Vector3=} scale - Output param.
   * @param {Quaternion=} rotationQ - Output param.
   * @param {Vector3=} translation - Output param.
   * @returns {bool} True if an anim transform is assigned to the given fragment.
   *      If so, it is written to the given out params. False otherwise (outparams not changed).
   */
  getAnimTransform(fragId, scale, rotationQ, translation) {
    if (!this.animxforms)
    return false;

    if (!this.isFlagSet(fragId, MeshFlags.MESH_MOVED))
    return false;

    var off = fragId * 10;
    var ax = this.animxforms;

    if (scale) {
      scale.x = ax[off];
      scale.y = ax[off + 1];
      scale.z = ax[off + 2];
    }

    if (rotationQ) {
      rotationQ.x = ax[off + 3];
      rotationQ.y = ax[off + 4];
      rotationQ.z = ax[off + 5];
      rotationQ.w = ax[off + 6];
    }

    if (translation) {
      translation.x = ax[off + 7];
      translation.y = ax[off + 8];
      translation.z = ax[off + 9];
    }

    return true;
  }

  /**
   * Returns world matrix of a fragment.
   * @param {number} index - Fragment ID.
   * @param {Matrix4} dstMtx - Out param to receive the matrix.
   */
  getOriginalWorldMatrix(index, dstMtx) {
    var i = index * 12;
    var cur = dstMtx.elements;
    var orig = this.transforms;

    if (orig) {
      // If this.transforms is defined, copy transform from this array
      // In this.transforms, we only store the upper 3 rows explicitly.
      // The last row is alway (0,0,0,1).
      cur[0] = orig[i];
      cur[1] = orig[i + 1];
      cur[2] = orig[i + 2];
      cur[3] = 0;
      cur[4] = orig[i + 3];
      cur[5] = orig[i + 4];
      cur[6] = orig[i + 5];
      cur[7] = 0;
      cur[8] = orig[i + 6];
      cur[9] = orig[i + 7];
      cur[10] = orig[i + 8];
      cur[11] = 0;
      cur[12] = orig[i + 9];
      cur[13] = orig[i + 10];
      cur[14] = orig[i + 11];
      cur[15] = 1;
    } else {
      dstMtx.identity();
    }
  }

  /**
   * Writes the model local matrix of an instance to target.
   * The local matrix results from original and optional animation transformation.
   * @param {number} instanceId
   * @param {Autodesk.Viewing.Math.Matrix4} target
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.getLocalMatrix
   */
  getLocalMatrix(instanceId, target) {
    this.getOriginalWorldMatrix(instanceId, target);

    // If the instance hasn't moved from its original location, just use that.
    if (this.isFlagSet(instanceId, MeshFlags.MESH_MOVED)) {
      // Otherwise construct the overall world matrix
      this.getAnimTransform(instanceId, _tmpScale, _tmpRot, _tmpPos);

      // compose matrix from pos, rotation, and scale
      tmp.compose(_tmpPos, _tmpRot, _tmpScale);

      // First apply original matrix (in target), then anim matrix (in tmp).
      // Note that tmp must be multiplied from left for this.
      target.multiplyMatrices(tmp, target);
    }
  }

  /**
   * Writes the final world matrix of an instance to target.
   * The world matrix results from original and optional animation transformation.
   * @param {number} instanceId
   * @param {Autodesk.Viewing.Math.Matrix4} target - Target matrix to receive the result.
   * @returns {Autodesk.Viewing.Math.Matrix4} The world matrix.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.getWorldMatrix
   */
  getWorldMatrix(instanceId, target) {
    this.getLocalMatrix(instanceId, target);

    // Apply optional model-transform
    if (this.matrix) {
      // Apply fragment matrix first (=target), then model matrix (=this.matrix).
      // Note that model matrix must be multiplied from left for this.
      target.multiplyMatrices(this.matrix, target);
    }

    return target;
  }

  setModelMatrix(matrix) {
    if (matrix) {
      this.matrix = this.matrix || new Matrix4();
      this.matrix.copy(matrix);
    } else {
      this.matrix = null;
    }
    this.invMatrix = null;

    // Only needed for WebGPU (otherwise, there are no callbacks).
    // Note that although this call changes the result of getWorldMatrix() for all fragments, the
    // _transformChangedCallbacks are not fired to avoid invoking per-fragment callbacks for the entire model.
    // Instead the _modelMatrixChangedCallbacks inform listeners the entire model has changed.
    for (const callback of this._modelMatrixChangedCallbacks) {
      callback();
    }
  }

  /**
   * Returns the world bounding box of an instance.
   * @param {number} instanceId
   * @param {Box3} target - result is saved here
   * @returns {Box3} The world bounding box of the instance.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.getWorldBounds
   */
  getWorldBounds(instanceId, target) {
    //Check if the world transform of the instance is unchanged from
    //the original LMV file -- in such case we can use the original
    //bounding box from the LMV package, which is presumably more precise (tighter)
    //than just transforming the model box.
    //This is important if we want to keep our bounding volume hierarchy efficient.
    if (this.boxes && !this.isFlagSet(instanceId, MeshFlags.MESH_MOVED)) {
      var b = this.boxes;
      var boffset = instanceId * 6;
      target.min.x = b[boffset];
      target.min.y = b[boffset + 1];
      target.min.z = b[boffset + 2];
      target.max.x = b[boffset + 3];
      target.max.y = b[boffset + 4];
      target.max.z = b[boffset + 5];

      // Consider optional model matrix if there is one
      if (this.matrix) {
        target.applyMatrix4(this.matrix);
      }

      return target;
    }

    // get original model box
    this.geoms.getModelBox(this.geomids[instanceId], target);

    if (this.viewBounds) {
      // Crop the box by the viewing bounds
      target.intersect(this.viewBounds);
    }

    if (!target.isEmpty()) {
      // apply world matrix to target.
      // Note that the worldMatrix includes the model matrix as well.
      this.getWorldMatrix(instanceId, _tmpMatrix);
      target.applyMatrix4(_tmpMatrix);
    } else {
      // target could be empty from intersect, but not on all axis.
      // Make sure all coordinates are really empty so we don't add anything irrelevant
      // when accumulating the result.
      target.makeEmpty();
    }

    return target;
  }

  /**
   * Writes the original (as loaded) world box to dstBox outparams. Does not take into account changes
   * to object position like explode/animation or model matrix.
   * @param {number} index - Fragment ID.
   * @param {Array} dstBox - Array where result is stored as 6 consecutive numbers
   */
  getOriginalWorldBounds(index, dstBox) {
    if (this.boxes) {
      var b = this.boxes;
      var boffset = index * 6;
      dstBox[0] = b[boffset];
      dstBox[1] = b[boffset + 1];
      dstBox[2] = b[boffset + 2];
      dstBox[3] = b[boffset + 3];
      dstBox[4] = b[boffset + 4];
      dstBox[5] = b[boffset + 5];
      return;
    }

    // get original model box
    this.geoms.getModelBox(this.geomids[index], _tmpBox);

    if (!_tmpBox.isEmpty()) {
      // apply world matrix to dstBox
      this.getOriginalWorldMatrix(index, _tmpMatrix);
      _tmpBox.applyMatrix4(_tmpMatrix);
    }

    dstBox[0] = _tmpBox.min.x;
    dstBox[1] = _tmpBox.min.y;
    dstBox[2] = _tmpBox.min.z;
    dstBox[3] = _tmpBox.max.x;
    dstBox[4] = _tmpBox.max.y;
    dstBox[5] = _tmpBox.max.z;
  }

  /**
   * Registers a callback that is invoked when the transform of an instance is changed.
   * Important: This is to get notified about individual instance changes, it does not fire when the model matrix changes.
   * @param {InstancesCallback} callback - The callback to invoke on the event.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.registerTransformChangedCallback
   */
  registerTransformChangedCallback(callback) {
    this._transformChangedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback that has previously been registered via `registerTransformChangedCallback`.
   * @param {InstancesCallback} callback - The callback to deregister.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.removeTransformChangedCallback
   */
  removeTransformChangedCallback(callback) {
    const index = this._transformChangedCallbacks.indexOf(callback);

    if (index !== -1) {
      this._transformChangedCallbacks.splice(index, 1);
    }
  }

  /**
   * Registers a callback that is invoked when instances are added via `add()`.
   * @param {InstancesCallback} callback
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.registerInstancesAddedCallback
   */
  registerInstancesAddedCallback(callback) {
    this._instancesAddedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback previously registered via `registerInstancesAddedCallback`.
   * @param {InstancesCallback} callback
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.removeInstancesAddedCallback
   */
  removeInstancesAddedCallback(callback) {
    const index = this._instancesAddedCallbacks.indexOf(callback);

    if (index !== -1) {
      this._instancesAddedCallbacks.splice(index, 1);
    }
  }

  /**
   * Registers a callback that is invoked when instances are removed via `remove()`.
   * @param {InstancesCallback} callback
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.registerInstancesRemovedCallback
   */
  registerInstancesRemovedCallback(callback) {
    this._instancesRemovedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback previously registered via `registerInstancesRemovedCallback`.
   * @param {InstancesCallback} callback
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.removeInstancesRemovedCallback
   */
  removeInstancesRemovedCallback(callback) {
    const index = this._instancesRemovedCallbacks.indexOf(callback);

    if (index !== -1) {
      this._instancesRemovedCallbacks.splice(index, 1);
    }
  }

  /**
   * Registers a callback that is invoked when the geometry of an instance is changed via `setGeometry()`.
   * @param {GeometryChangedCallback} callback
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.registerGeometryChangedCallback
   */
  registerGeometryChangedCallback(callback) {
    this._geometryChangedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback previously registered via `registerGeometryChangedCallback`.
   * @param {GeometryChangedCallback} callback
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.removeGeometryChangedCallback
   */
  removeGeometryChangedCallback(callback) {
    const index = this._geometryChangedCallbacks.indexOf(callback);

    if (index !== -1) {
      this._geometryChangedCallbacks.splice(index, 1);
    }
  }

  //////////////////////////////////////////////////////////////
  // DYNAMIC
  //////////////////////////////////////////////////////////////

  /**
   * Add a new instance using the given geometry, materials and transform.
   * If the geometry and material are not already in the model, they will be added.
   * This transfers ownership of the geometry and material to the instance collection and the objects can change in the process.
   *
   * @param {Autodesk.Viewing.Scene.BufferGeometry} geometry The geometry of the instance.
   * @param {Autodesk.Viewing.Scene.Material} material The material of the instance.
   * @param {Autodesk.Viewing.Scene.Matrix4} [transform] The transformation matrix of the instance. If not provided, it will be set to the identity matrix.
   * @param {Autodesk.Viewing.Scene.Box3} [bbox] The bounding box of the instance relative to the model, if not provided, it will be computed.
   * @returns {number} The id of the new instance, -1 if the instance could not be added.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.add
   */
  add(geometry, material, transform, bbox) {
    if (this.isFixedSize) {
      logger.error("Attempting to add an instance to a fixed size InstanceCollection3D. This is not supported.");
      return -1;
    }

    if (!transform) {
      transform = new Matrix4();
    }

    this.addMaterial(material);
    this.addGeometry(geometry);
    this.geoms.addInstance(geometry.svfid);

    const meshInfo = this._viewer3DImpl.setupMesh(this.model, geometry, material.id, transform);

    if (!bbox) {
      meshInfo.bbox = this._calculateBBox(geometry);
      meshInfo.bbox.applyMatrix4(transform);
    } else {
      meshInfo.bbox = bbox;
    }

    const instanceId = this._instanceIdGenerator.reserveId();

    this.model.activateFragment(instanceId, meshInfo, true);
    this.setFragOff(instanceId, false);
    this.setFlagFragment(instanceId, MeshFlags.MESH_NOTLOADED, false);

    // Update explicit counters
    this.fragments.length++;
    this.primitiveCount += geometry.polyCount;

    this._instancesAddedCallbacks.forEach((callback) => callback(instanceId));

    this.invalidateBBoxes();

    return instanceId;
  }

  /**
   * Removes the instances from the collection.
   * Unused geometries remain in the model and need to be removed manually.
   * @param {number|number[]} instanceIds The id or ids of instances to remove.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.remove
   */
  remove(instanceIds) {
    if (!Array.isArray(instanceIds))
    instanceIds = [instanceIds];

    for (let i = 0; i < instanceIds.length; i++) {
      const instanceId = instanceIds[i];
      // Check if fragment exists by checking if we can get its geometry
      const geometry = this.getGeometry(instanceId);
      if (geometry) {
        this.primitiveCount -= geometry.polyCount;
        this.fragments.length--;
        this._instanceIdGenerator.releaseId(instanceId);
      }
    }
    this.unload(instanceIds);

    this._instancesRemovedCallbacks.forEach((callback) => callback(instanceIds));

    this.invalidateBBoxes();
  }

  /**
   * Add a material that can be referenced by an instance in the model.
   * This transfers ownership of the material to the instance collection and the object can change in the process.
   * A material is only added once and ignored after that.
   * @param {Autodesk.Viewing.Scene.Material} material The material to add.
   * @returns {boolean} True if the material was added.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.addMaterial
   */
  addMaterial(material) {
    if (material.svfMatId !== undefined)
    return false;

    // Currently, we only support packed normals for materials.
    if (!material.packedNormals) {
      material.packedNormals = true;
      material.needsUpdate = true;
    }

    const matId = material.id.toString();
    const hash = this._viewer3DImpl.matman()._getMaterialHash(this.model.id, matId);
    if (!this._viewer3DImpl.matman().hasMaterial(hash)) {
      this._viewer3DImpl.matman().addMaterial(hash, material, true);
      material.svfMatId = matId;

      // Load textures specified via textureMaps for Scene API materials.
      if (material.textureMaps) {
        TextureLoader.loadMaterialTextures(this.model, material, this._viewer3DImpl);
      }
    } else {
      console.error("Failed to add material to the model.");
      return false;
    }

    return true;
  }

  /**
   * Adds a new geometry to the model and computes its bounding box if it is not already computed.
   * This transfers ownership of the geometry to the instance collection and the object can change in the process.
   * The geometry is only added once and ignored after that.
   * @param {Autodesk.Viewing.Scene.BufferGeometry} geometry
   * @returns {Autodesk.Viewing.Scene.BufferGeometry} The geometry.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.addGeometry
   */
  addGeometry(geometry) {
    if (geometry.svfid === undefined) {
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox();
      }

      if (!geometry.vb) {
        // We want interleaved geometry with packed normals, so we need to interleave it if it is not already interleaved
        BufferGeometryUtils.interleaveGeometry(geometry, true);
        geometry.attributes = geometry.attributesInterleaved;
      }

      this.geoms.addGeometry(geometry, 0);
    }
    return geometry;
  }

  /**
   * Set the geometry of an instance. It will replace the current instance geometry with the new one.
   * If not already added, the geometry will be added to the collection.
   * @param {number} instanceId The id of the instance to set the geometry of.
   * @param {Autodesk.Viewing.Scene.BufferGeometry} geometry The geometry to set.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.setGeometry
   */
  setGeometry(instanceId, geometry) {
    // Update primitive and instance counts
    this.primitiveCount -= this.getGeometry(instanceId).polyCount - geometry.polyCount;
    this.geoms.removeInstance(this.geomids[instanceId], this._viewer3DImpl.geomCache(false));

    this.addGeometry(geometry);
    this.geoms.addInstance(geometry.svfid);
    this.geomids[instanceId] = geometry.svfid;

    // Update the bounding box of the instance
    const mtx = new Matrix4();
    this.getOriginalWorldMatrix(instanceId, mtx);
    this._updateBBox(instanceId, mtx, geometry.boundingBox);

    this.invalidateBBoxes();

    for (const callback of this._geometryChangedCallbacks) {
      callback(instanceId, geometry);
    }
  }

  resize(size) {
    // The various data arrays need to be re-sized if the fragment is new so we have to do it manually in case this happens.
    if (this.vizflags.length <= size) {
      if (this.isFixedSize) {
        logger.warn("Attempting to resize a fragments list that was initialized with fixed data. This will have a performance impact.");
        this.isFixedSize = false;
      }

      // determine new length of all per-fragmentId arrays
      let nlen = Math.ceil(1.5 * Math.max(this.vizflags.length, size)) || 1;

      super.resize(nlen);

      // re-allocate other per-fragmentId arrays...
      const ntransforms = new Float32Array(nlen * 12);
      if (this.transforms) {
        ntransforms.set(this.transforms);
      }
      this.transforms = ntransforms;
      this.fragments.transforms = this.transforms;

      const nboxes = new Float32Array(nlen * 6);
      if (this.boxes) {
        nboxes.set(this.boxes);
      }
      this.boxes = nboxes;
      this.fragments.boxes = this.boxes;

      const nids = new Int32Array(nlen);
      nids.set(this.geomids);
      this.geomids = nids;

      const nmids = new Int32Array(nlen);
      nmids.set(this.materialids);
      this.materialids = nmids;
    } else {
      console.error("Downsizing an InstanceCollection3D is not supported yet");
    }
  }

  /**
   * Get the transform of an instance.
   * @param {number} instanceId The id of the instance to get the transform of.
   * @param {Autodesk.Viewing.Scene.Matrix4} target The matrix to store the transform in.
   * @returns {Autodesk.Viewing.Scene.Matrix4} The transform of the instance.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.getTransform
   */
  getTransform(instanceId, target) {
    return this.getOriginalWorldMatrix(instanceId, target);
  }

  /**
   * Set the transform of an instance.
   * @param {number} instanceId The id of the instance to set the transform of.
   * @param {Autodesk.Viewing.Scene.Matrix4} transform The transform to set.
   *
   * @alias Autodesk.Viewing.Scene.InstanceCollection3D.setTransform
   */
  setTransform(instanceId, transform) {
    this.setOriginalWorldMatrix(instanceId, transform);

    this.invalidateBBoxes();
  }

  /**
   * Invalidates the model bounding box.
   */
  invalidateBBoxes() {
    this.#modelBBoxDirty = true;
    this.model.invalidateBBoxes();
  }

  /**
   * @param geometry
   * @param dst
   * @private
   */
  _calculateBBox(geometry) {
    const box = new Box3();
    VertexEnumerator.enumMeshVertices(geometry, function (pos) {
      box.expandByPoint(pos);
    });
    return box;
  }

  /**
   * Ensures the model bounding box is up to date.
   * Call this before accessing the model's bounding box if transforms may have changed.
   */
  ensureModelBBoxUpToDate() {
    if (this.#modelBBoxDirty) {
      this.#modelBBoxDirty = false;

      const originalBbox = this.model._getModelBBoxOriginal();
      originalBbox.makeEmpty();

      const tmpBbox = new Box3();
      for (let i = 0; i < this.fragments.length; i++) {
        this.getWorldBounds(i, tmpBbox);
        originalBbox.union(tmpBbox);
      }
    }
  }

  /**
   * Updates the bounding box of an instance. If no bounding box is provided, the model box will be used together with the transform to compute the new bounding box.
   * @param {number} instanceId - The id of the instance to update the bounding box of.
   * @param {Autodesk.Viewing.Scene.Matrix4} transform The transform to apply to the bounding box.
   * @param {Autodesk.Viewing.Math.Box3} [bbox] The new bounding box of the instance.
   * @param {boolean} [isWorldBbox = false] Indicates whether the bounding box is already in world space.
   *
   * @internal
   */
  _updateBBox(instanceId, transform, bbox, isWorldBbox = false) {
    if (bbox) {
      _tmpBox.copy(bbox);
    } else {
      this.geoms.getModelBox(this.geomids[instanceId], _tmpBox);
    }

    if (!isWorldBbox && !_tmpBox.isEmpty()) {
      // Transform the local BB to world
      _tmpBox.applyMatrix4(transform);
    }

    // Write bounding box to this.boxes
    const boffset = instanceId * 6;
    const bb = this.boxes;
    bb[boffset] = _tmpBox.min.x;
    bb[boffset + 1] = _tmpBox.min.y;
    bb[boffset + 2] = _tmpBox.min.z;
    bb[boffset + 3] = _tmpBox.max.x;
    bb[boffset + 4] = _tmpBox.max.y;
    bb[boffset + 5] = _tmpBox.max.z;
  }

  setOriginalWorldMatrix(index, mtx) {
    var i = index * 12;
    var cur = mtx.elements;
    var orig = this.transforms;

    if (orig) {
      // If this.transforms is defined, copy new transform into it.
      // In this.transforms, we only store the upper 3 rows explicitly.
      // The last row is alway (0,0,0,1).
      orig[i] = cur[0];
      orig[i + 1] = cur[1];
      orig[i + 2] = cur[2];

      orig[i + 3] = cur[4];
      orig[i + 4] = cur[5];
      orig[i + 5] = cur[6];

      orig[i + 6] = cur[8];
      orig[i + 7] = cur[9];
      orig[i + 8] = cur[10];

      orig[i + 9] = cur[12];
      orig[i + 10] = cur[13];
      orig[i + 11] = cur[14];
    }

    this._updateBBox(index, mtx, this.getGeometry(index).boundingBox);

    for (const callback of this._transformChangedCallbacks) {
      callback(index);
    }
  }

  /**
   * [WebGPU Only] Optional hover-highlighting effect for object categories.
   *  - A callback maps each dbId to a highlighting category (16-Bit signed integer)
   *  - This allows to efficiently apply hover-highlighting of whole categories via BlendSettings.
   *  - Note that you must set the callback again whenever its return values would change for any dbId.
   *  - 0 is reserved for "no highlight", so objects with category 0 cannot be highlighted.
   * @param {HighlightingCategoryMappingCallback} callback - A function that maps dbIDs to highlighting categories.
   */
  setHighlightingCategoryMapping(callback) {
    this._highlightingCategoryMapping = callback;

    for (const changeCallback of this._highlightingCategoryChangedCallbacks) {
      changeCallback();
    }
  }

  /**
   * Registers a callback that is invoked when the highlighting category mapping changes.
   * @param {function} callback - The callback to invoke on the event.
   */
  registerHighlightingMappingChangedCallback(callback) {
    this._highlightingCategoryChangedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback that has previously been registered via `registerHighlightingMappingChangedCallback`.
   * @param {function} callback - The callback to deregister.
   */
  removeHighlightingCategoryChangedCallback(callback) {
    const index = this._highlightingCategoryChangedCallbacks.indexOf(callback);

    if (index !== -1) {
      this._highlightingCategoryChangedCallbacks.splice(index, 1);
    }
  }

  /**
   * Returns the scene graph root node for the model. If 'create' is true and the scene graph does not exist, it will be created.
   * @param {boolean} create - Whether to create the scene graph if it doesn't exist.
   * @returns {Node3D|null} The scene graph root node. Null if the scene graph is not created.
   */
  getSceneGraph(create = true) {
    if (!this.#sceneGraph && create) {
      this.#sceneGraph = new Node3D();
      this.#sceneGraph._initialize(this);
    }

    return this.#sceneGraph;
  }
}